/**
 * @file shop-columns.tsx
 * @description Колонки списка «Магазины» для `ResponsiveList` (phase19.28
 * этап А; uiux-platform-shops §2): одно описание — таблица от 1024 и
 * строки-карточки до 1024.
 *
 * DNS / прокси / HTTPS — только если API отдаёт проверку основного адреса
 * (`primaryCheck`): иначе колонок нет, а не «—» во всех строках. Выручки и
 * заказов здесь нет (В4): только служебные статусы.
 */

import type { ShopListItem } from "../../../lib/api-platform-shops";
import { SITE_STATE_META, extraAddressesLabel, ownerLabel, primaryHostLabel, primaryLastCheck, siteState } from "../../../lib/platform/shop-list-filter";
import { formatPlatformDate } from "../../../lib/platform/shop-format";
import type { ListColumn } from "../../ui/responsive-list-types";
import { StatusPill } from "../../ui/StatusPill";
import { DomainCheckMark, DomainCheckMarks } from "./DomainCheckMarks";
import { ShopPrimaryHost } from "./ShopPrimaryHost";

const checkColumn = (key: "dns" | "proxy" | "cert", header: string): ListColumn<ShopListItem> => ({
  key,
  header,
  width: key === "cert" ? "w-[96px]" : "w-[72px]",
  cell: (shop) => <DomainCheckMark check={primaryLastCheck(shop)} part={key} />,
});

export function shopColumns(withChecks: boolean): ListColumn<ShopListItem>[] {
  return [
    {
      key: "name",
      header: "Магазин",
      compact: "title",
      cell: (shop) => (
        <span className="flex min-w-0 flex-col">
          <span className="line-clamp-2 font-medium" title={shop.displayName}>{shop.displayName}</span>
          <span className="type-caption text-on-surface-variant">{shop.slug}</span>
        </span>
      ),
      compactCell: (shop) => (
        <span className="line-clamp-2" title={shop.displayName}>
          {shop.displayName} <span className="type-caption text-on-surface-variant">· {shop.slug}</span>
        </span>
      ),
    },
    {
      key: "host",
      header: "Главный адрес",
      compact: "subtitle",
      cell: (shop) => <ShopPrimaryHost shop={shop} />,
      compactCell: (shop) => {
        const extra = extraAddressesLabel(shop);
        const host = primaryHostLabel(shop);
        return extra ? `${host} ${extra}` : host;
      },
    },
    ...(withChecks
      ? [checkColumn("dns", "DNS"), checkColumn("proxy", "Прокси"), checkColumn("cert", "HTTPS")]
      : []),
    {
      key: "site",
      header: "Сайт",
      width: "w-[160px]",
      compact: "trailing",
      cell: (shop) => <StatusPill meta={SITE_STATE_META[siteState(shop)]} />,
    },
    {
      key: "owner",
      header: "Владелец",
      width: "w-[180px]",
      compact: "meta",
      cell: (shop) => <span className="break-words">{ownerLabel(shop)}</span>,
      // Одна строка «Владелец Анна» / «Владельца нет», проверки — своей строкой
      // (без висячей « · » в конце, QA D-B1).
      compactCell: (shop) => (
        <span className="flex flex-col gap-0.5">
          <span>{shop.owners.length > 0 ? `Владелец ${ownerLabel(shop)}` : "Владельца нет"}</span>
          {withChecks ? <DomainCheckMarks check={primaryLastCheck(shop)} /> : null}
        </span>
      ),
    },
    {
      key: "created",
      header: "Создан",
      width: "w-[96px]",
      cell: (shop) => <span className="tabular-nums">{formatPlatformDate(shop.createdAt)}</span>,
    },
  ];
}
